<script lang="ts">
  import type { ComposerEditor } from './composer-editor';

  interface Props {
    editor: ComposerEditor;
    placeholder: string;
    showPlaceholder: boolean;
    expanded?: boolean;
  }

  let { editor, placeholder, showPlaceholder, expanded = false }: Props = $props();

  function mount(node: HTMLElement): () => void {
    const detach = editor.mount(node);
    $effect(() => {
      editor.syncEditable();
    });
    $effect(() => {
      editor.syncLabel();
    });
    $effect(() => {
      editor.syncKeyHint();
    });
    return detach;
  }
</script>

<div
  class={['editor', { empty: showPlaceholder, expanded }]}
  data-placeholder={placeholder}
  {@attach mount}
></div>

<style>
  .editor {
    flex: 1;
    font-size: max(var(--font-size-editor), var(--font-size-input-min));
    max-height: 10rem;
    min-height: var(--target);
    min-width: 0;
    overflow-y: auto;
    padding: var(--space-200);
    padding-bottom: var(--space-100);
    position: relative;
  }

  /* `white-space` comes from prosemirror.css, imported by `composer-editor.ts`. */
  .editor :global([contenteditable='true']) {
    outline: 0;
    overflow-wrap: anywhere;
  }

  .editor :global(.keyboard-reset) {
    border: 0;
    inset: 0;
    opacity: 0;
    padding: 0;
    pointer-events: none;
    position: absolute;
    resize: none;
  }

  .editor :global([contenteditable='true'] p) {
    margin: 0;
  }

  @media (width >= 48rem) and (any-pointer: fine) {
    .editor {
      max-height: clamp(10rem, 30dvh, 20rem);
    }
  }

  .editor.expanded {
    height: clamp(10rem, calc((100dvh - var(--keyboard-overlap)) / 2), 40rem);
    max-height: none;
  }

  .editor :global([contenteditable='true'] p + p) {
    margin-block-start: 1lh;
  }

  .editor :global(h1),
  .editor :global(h2),
  .editor :global(h3) {
    font-weight: var(--font-weight-bold);
    line-height: inherit;
    margin: 0;
  }

  .editor :global(h1) {
    font-size: var(--font-size-heading);
  }

  .editor :global(h2) {
    font-size: var(--font-size-heading);
  }

  .editor :global(h3) {
    font-size: inherit;
  }

  .editor :global(sub[data-md='-#']) {
    color: var(--surface-var-on-container);
    display: block;
    font-size: var(--font-size-small);
  }

  .editor :global(blockquote) {
    border-inline-start: calc(var(--border-width) * 3) solid var(--primary-main);
    margin: 0;
    padding-inline-start: var(--space-300);
  }

  .editor :global(ul),
  .editor :global(ol) {
    margin: 0;
    padding-inline-start: var(--space-400);
  }

  .editor :global(:not(pre) > code) {
    background: var(--bg-container);
    border: var(--border-width) solid var(--bg-container-line);
    border-radius: var(--radii-300);
    color: var(--bg-on-container);
    font-family: var(--font-family-mono);
    font-variant-ligatures: none;
    padding: 0 var(--space-050);
  }

  .editor :global(pre) {
    background: var(--bg-container);
    border: var(--border-width) solid var(--bg-container-line);
    border-radius: var(--radius);
    color: var(--bg-on-container);
    font-family: var(--font-family-mono);
    font-variant-ligatures: none;
    margin: 0;
    overflow-x: auto;
    padding: var(--space-200);
  }

  .editor :global(pre code) {
    font-family: var(--font-family-mono);
    font-size: var(--font-size-small);
    font-variant-ligatures: none;
  }

  :root[data-code-ligatures='on'] .editor :global(code) {
    font-variant-ligatures: normal;
  }

  /* ProseMirror's caret hack; a `pre` already breaks on the newline itself. */
  /* stylelint-disable-next-line selector-class-pattern */
  .editor :global(pre br.ProseMirror-trailingBreak:not(:only-child)) {
    display: none;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .editor :global(img.ProseMirror-separator) {
    /* stylelint-disable-next-line declaration-no-important */
    display: inline-block !important;
    height: 1lh;
    vertical-align: bottom;
    width: 0;
  }

  .editor :global(.composer-mention-label)::before {
    content: attr(data-label);
  }

  .editor :global(.composer-mention-remove)::before {
    content: '×';
  }

  .editor :global(pre[data-language])::before {
    color: var(--surface-var-on-container);
    content: attr(data-language);
    display: block;
    font-size: var(--font-size-small);
    margin-bottom: var(--space-100);
  }

  .editor :global([data-mx-spoiler]) {
    background: var(--surface-container-active);
    border-radius: var(--radii-300);
    color: var(--surface-on-container);
    padding: 0 var(--space-050);
  }

  .editor :global(a) {
    color: var(--tc-link, var(--primary-main));
    text-decoration: underline;
  }

  .editor :global(hr) {
    background: var(--surface-container-line);
    border: 0;
    height: var(--border-width);
    margin: var(--space-200) 0;
  }

  .editor :global(table) {
    border-collapse: collapse;
    margin: var(--space-100) 0;
  }

  .editor :global(th),
  .editor :global(td) {
    border: var(--border-width) solid var(--surface-container-line);
    padding: var(--space-050) var(--space-150);
    text-align: start;
  }

  .editor :global(th) {
    background: var(--surface-var-container);
    color: var(--surface-var-on-container);
    font-weight: var(--font-weight-bold);
  }

  .editor :global(summary) {
    cursor: default;
    font-weight: var(--font-weight-bold);
  }

  .editor :global(dt) {
    font-weight: var(--font-weight-bold);
  }

  .editor :global(dd) {
    margin-inline-start: var(--space-600);
  }

  .editor :global([data-mx-maths]) {
    font-family: var(--font-family-mono);
    font-variant-ligatures: none;
  }

  .editor :global([data-sable-room-ping]) {
    background: var(--warn-container);
    border-radius: var(--radius-pill);
    color: var(--warn-on-container);
    padding: 0 var(--space-150);
  }

  .editor :global(.composer-image img) {
    max-height: 8rem;
    max-width: 100%;
    vertical-align: middle;
  }

  .editor.empty::before {
    color: var(--surface-var-on-container);
    content: attr(data-placeholder);
    inset-inline: var(--space-200);
    opacity: var(--opacity-placeholder);
    overflow: hidden;
    pointer-events: none;
    position: absolute;
    text-overflow: ellipsis;
    top: var(--space-200);
    white-space: nowrap;
  }

  .editor :global(.composer-mention),
  .editor :global(.composer-time) {
    background: var(--primary-container);
    border-radius: var(--radius-pill);
    color: var(--primary-on-container);
    padding: 0 var(--space-150);
  }

  .editor :global(.composer-mention) {
    align-items: center;
    display: inline-flex;
    gap: var(--space-050);
  }

  .editor :global(.composer-mention-remove) {
    appearance: none;
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    display: inline;
    font: inherit;
    line-height: 1;
    margin: 0;
    padding: 0;
  }

  /* ProseMirror's own class; the themed `.selected` below paints instead. */
  /* stylelint-disable-next-line selector-class-pattern */
  .editor :global(.ProseMirror-selectednode) {
    outline: 0;
  }

  .editor :global(.composer-mention.selected),
  .editor :global(.composer-time[class~='ProseMirror-selectednode']) {
    box-shadow: 0 0 0 var(--focus-ring-width) var(--focus-ring);
  }

  .editor :global(.composer-mention-remove:focus-visible) {
    outline: var(--focus-ring-width) solid var(--focus-ring);
    outline-offset: var(--space-050);
  }

  .editor :global(.composer-emoticon img) {
    height: 1.5em;
    vertical-align: middle;
    width: auto;
  }

  .editor :global(.composer-emoticon.selected) {
    background: var(--surface-container-active);
    border-radius: var(--radius);
  }
</style>
